import { type FunctionComponent, type ReactNode, useRef } from 'react';

import { Button } from '@dr.pogodin/react-utils';

import { useImageUrl } from 'hooks';

import { type AssetT, E_FILE, P_API_ASSETS } from 'utils';

import IconTrash from 'assets/images/icon-trash-16px.svg';

import buttonTheme from 'themes/Button/admin/icon.scss';

import { FileIcon } from './FileIcon';

import buttonFlexTheme from './button-flex.scss';

import S from './style.scss';

type HeapT = {
  dragIcon?: HTMLDivElement & HTMLImageElement;
};

type PropsT = {
  data: AssetT | File;
  onRemove: () => void;
  onUse: () => void;
};

export const dragging: {
  data?: AssetT | File;
} = {};

export const Item: FunctionComponent<PropsT> = ({ data, onRemove, onUse }) => {
  let preview: ReactNode;

  let containerClass = S.container;

  const dataUrl = useImageUrl(data);

  let usableAsImage = false;

  const { current: heap } = useRef<HeapT>({});

  const previewRef = useRef<HTMLDivElement & HTMLImageElement>(null);

  if (data instanceof File) {
    if (data.type.startsWith('audio/')) {
      preview = <FileIcon kind="AUDIO" name={data.name} ref={previewRef} />;
    } else if (data.type.startsWith('image/')) {
      containerClass += ` ${S.image}`;
      preview = <img alt="Preview" ref={previewRef} src={dataUrl} />;
      usableAsImage = true;
    } else if (data.type.startsWith('video/')) {
      preview = <FileIcon kind="VIDEO" name={data.name} ref={previewRef} />;
    } else {
      preview = <FileIcon kind="DOCUMENT" name={data.name} ref={previewRef} />;
    }
  } else {
    switch (data.kind) {
      case 'AUDIO':
        preview = (
          <FileIcon
            kind="AUDIO"
            name={data.storageProperties.name}
            ref={previewRef}
          />
        );
        break;
      case 'DOCUMENT':
        preview = (
          <FileIcon
            kind="DOCUMENT"
            name={data.storageProperties.name}
            ref={previewRef}
          />
        );
        break;
      case 'IMAGE':
        containerClass += ` ${S.image}`;
        preview = (
          <img
            alt="Preview"
            ref={previewRef}
            src={`${P_API_ASSETS}/${data.id}${E_FILE}?s=360`}
          />
        );
        usableAsImage = true;
        break;
      case 'VIDEO':
      default:
    }
  }

  const cleanUpDrag = () => {
    delete dragging.data;
    heap.dragIcon?.remove();
    delete heap.dragIcon;
  };

  return (
    <div
      className={containerClass}
      draggable
      onDragEnd={cleanUpDrag}
      onDragStart={(e) => {
        let icon = previewRef.current;
        if (!icon) throw Error('Internal error');

        const { offsetHeight, offsetWidth } = icon;

        icon = icon.cloneNode(true) as typeof icon;
        icon.style.height = `${offsetHeight}px`;
        icon.style.objectFit = 'contain';
        icon.style.position = 'fixed';
        icon.style.top = '100dvh';
        icon.style.left = '100dvw';
        icon.style.width = `${offsetWidth}px`;
        document.body.appendChild(icon);

        heap.dragIcon = icon;

        dragging.data = data;
        e.dataTransfer.setDragImage(icon, offsetWidth / 2, offsetHeight / 2);
      }}
    >
      {preview}
      <div styleName="buttons">
        {usableAsImage
          ? <Button onClick={onUse} theme={buttonFlexTheme}>Use</Button>
          : null}
        <Button
          onClick={onRemove}
          theme={buttonTheme}
        >
          <IconTrash />
        </Button>
      </div>
    </div>
  );
};
